/**
 * useToggleFavorite Hook
 *
 * React Query hook for toggling an application as favorite/unfavorite for a user.
 * Now includes optimistic updates and AuthContext synchronization.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userService } from '../../../api/user.service';
import type { UserSanitized } from '../../../types/user.types';
import { getErrorMessage } from '../../../lib/utils/errors';
import { useAuth } from '../../../contexts/AuthContext';

interface ToggleFavoriteParams {
  tenantId: string;
  userId: string;
  applicationId: string;
  isFavorite: boolean;
}

interface UseToggleFavoriteOptions {
  onSuccess?: (user: UserSanitized) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for toggling favorite application with optimistic updates
 */
export const useToggleFavorite = (options?: UseToggleFavoriteOptions) => {
  const queryClient = useQueryClient();
  const { user: currentUser, updateUser } = useAuth();

  return useMutation<UserSanitized, Error, ToggleFavoriteParams>({
    mutationFn: async ({ tenantId, userId, applicationId, isFavorite }) => {
      if (isFavorite) {
        // Remove from favorites
        return await userService.removeFavoriteApplication(tenantId, userId, applicationId);
      } else {
        // Add to favorites
        return await userService.addFavoriteApplication(tenantId, userId, applicationId);
      }
    },
    onMutate: async ({ userId, applicationId, isFavorite }) => {
      // Cancel outgoing user queries to avoid race conditions
      await queryClient.cancelQueries({ queryKey: ['user'] });
      await queryClient.cancelQueries({ queryKey: ['users'] });

      // Snapshot the current user state for rollback
      const previousUser = currentUser;

      // Optimistically update AuthContext if this is the current user
      if (currentUser && currentUser.userId === userId) {
        const currentFavorites = currentUser.favoriteApplications || [];
        const newFavorites = isFavorite
          ? currentFavorites.filter((id) => id !== applicationId)
          : [...currentFavorites, applicationId];

        const optimisticUser: UserSanitized = {
          ...currentUser,
          favoriteApplications: newFavorites,
        };

        updateUser(optimisticUser);

        console.log('[useToggleFavorite] Optimistic update applied:', {
          action: isFavorite ? 'removed' : 'added',
          applicationId,
          totalFavorites: newFavorites.length,
        });
      }

      return { previousUser };
    },
    onSuccess: (user, variables) => {
      // Update AuthContext with confirmed data from server
      if (currentUser && currentUser.userId === variables.userId) {
        updateUser(user);
      }

      // Invalidate user queries to refresh data elsewhere
      queryClient.invalidateQueries({ queryKey: ['users', variables.tenantId] });
      queryClient.invalidateQueries({ queryKey: ['user', variables.tenantId, variables.userId] });

      console.log('[useToggleFavorite] Favorite toggled successfully', {
        userId: variables.userId,
        applicationId: variables.applicationId,
        action: variables.isFavorite ? 'removed' : 'added',
        totalFavorites: user.favoriteApplications?.length || 0,
      });

      options?.onSuccess?.(user);
    },
    onError: (error, variables, context) => {
      // Rollback optimistic update on error
      if (context?.previousUser && currentUser && currentUser.userId === variables.userId) {
        updateUser(context.previousUser);

        console.log('[useToggleFavorite] Rolled back optimistic update due to error');
      }

      console.error('[useToggleFavorite] Failed to toggle favorite:', {
        error: getErrorMessage(error),
        userId: variables.userId,
        applicationId: variables.applicationId,
      });

      options?.onError?.(error);
    },
  });
};
